/**
 * ThemeSection — one row in the Explore tab.
 *
 * Two-column card: left holds the icon + title + "covers" tagline + verdict
 * paragraph; right is an auto-fill grid of ElementTiles for the theme's
 * signals (sorted by value desc by buildMarketerViewData).
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import type { ScoreNode, ThemeBucket } from '../types';
import { Card } from '../atoms';
import { ElementTile } from '../atoms';

export interface ThemeSectionProps {
  theme: ThemeBucket;
  onOpen: (node: ScoreNode) => void;
}

export const ThemeSection: React.FunctionComponent<ThemeSectionProps> = ({
  theme,
  onOpen,
}) => (
  <section id={`theme-${theme.id}`} style={{ paddingTop: 40 }}>
    <Card pad={0} style={{ overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '0.86fr 1.14fr' }}>
        {/* Verdict */}
        <div
          style={{
            padding: '30px 32px',
            borderRight: '1px solid #E5E7EB',
          }}
        >
          <span
            style={{
              width: 42,
              height: 42,
              borderRadius: 12,
              background: theme.tone,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <theme.icon size={20} strokeWidth={2} />
          </span>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: '#0F172A',
              margin: '16px 0 0',
              lineHeight: 1.12,
            }}
          >
            {theme.title}
          </h2>
          <div style={{ fontSize: 13, color: '#94A3B8', marginTop: 4 }}>{theme.covers}</div>
          <p
            style={{
              fontSize: 14.5,
              lineHeight: 1.62,
              color: '#64748B',
              marginTop: 18,
            }}
          >
            {theme.verdict}
          </p>
        </div>
        {/* Tiles */}
        <div style={{ padding: '26px 30px', background: '#FAFBFC' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 14,
              gap: 12,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#94A3B8',
                whiteSpace: 'nowrap',
              }}
            >
              <FormattedMessage
                id="marketerView.explore.elementsCount"
                defaultMessage="The elements ({n})"
                values={{ n: theme.signals.length }}
              />
            </span>
            <span
              style={{
                fontSize: 11,
                color: '#94A3B8',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              <FormattedMessage
                id="marketerView.explore.tapAny"
                defaultMessage="tap any element to dig in"
              />
            </span>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(148px, 1fr))',
              gap: 11,
            }}
          >
            {theme.signals.map((s) => (
              <ElementTile
                key={s.canonical}
                node={s}
                onClick={() => onOpen(s)}
              />
            ))}
          </div>
        </div>
      </div>
    </Card>
  </section>
);
